import * as PIXI from 'pixi.js';
import { useMapStore } from '../stores/mapStore';
import type { BattleMap } from '@machina/core';

/**
 * MapBackgroundLayer
 *
 * Renders the battle map texture as a background layer on the canvas.
 * Designed for current 2D preview while supporting future Unreal integration.
 *
 * The map uses world units (feet), which we convert to pixels using pixelsPerFoot.
 *
 * Transition state machine:
 *   IDLE → BLUR_OUT (300ms) → WIPE_REVEAL (600ms) → IDLE
 *             ↓ (no prev)      ↓ (load failed)
 *         FADE_IN (400ms)    PLACEHOLDER (quiet)
 */

type TransitionState = 'idle' | 'blur_out' | 'wipe_reveal' | 'fade_in';

interface SpriteBounds {
  width: number;
  height: number;
  originX: number;
  originY: number;
}

// Cubic ease-in-out
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// Cubic ease-out
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

const BLUR_DURATION_MS = 300;
const WIPE_DURATION_MS = 600;
const FADE_DURATION_MS = 400;
const MAX_BLUR_STRENGTH = 8;

export class MapBackgroundLayer {
  public container: PIXI.Container;

  private backgroundSprite: PIXI.Sprite | null = null;
  private unsubscribe: (() => void) | null = null;
  private pixelsPerFoot: number;
  private loadGeneration: number = 0;
  private currentTextureUrl: string | null = null;
  private pendingTextureUrl: string | null = null; // URL currently being loaded (prevents double-loads)

  // Transition state
  private transitionState: TransitionState = 'idle';
  private transitionTickerFn: (() => void) | null = null;
  private transitionStartTime: number = 0;
  private previousSprite: PIXI.Sprite | null = null;
  private blurFilter: PIXI.BlurFilter | null = null;
  private wipeMask: PIXI.Graphics | null = null;
  private pendingSprite: PIXI.Sprite | null = null;
  private pendingBounds: SpriteBounds | null = null;
  private blurOutComplete: boolean = false;
  private newTextureReady: boolean = false;

  constructor(pixelsPerFoot: number = 10) {
    this.pixelsPerFoot = pixelsPerFoot;
    this.container = new PIXI.Container();
    this.container.sortableChildren = true;

    // Subscribe to map store
    this.subscribeToStore();
  }

  private subscribeToStore(): void {
    // Initial render from current state
    const { activeMap } = useMapStore.getState();
    this.render(activeMap);

    // Watch for activeMap changes via Zustand subscription.
    // This is more reliable than React useEffect for first-load timing —
    // fires synchronously when store updates, no render-cycle dependency.
    // render() has a redundant-render check (same URL + sprite exists + idle)
    // so duplicate calls from both subscription and useEffect are harmless.
    let lastMap = activeMap;
    this.unsubscribe = useMapStore.subscribe((state) => {
      if (state.activeMap !== lastMap) {
        lastMap = state.activeMap;
        this.render(state.activeMap);
      }
    });
  }

  /**
   * Render the map background with cinematic transition
   */
  private render(map: BattleMap | null): void {
    console.log('[MapBackgroundLayer] render() called, map:', map?.name, 'hasTexture:', !!map?.baseTexture);

    const newUrl = map?.baseTexture?.colorUrl ?? null;

    // Skip redundant renders — same texture URL already displayed and idle
    if (newUrl && newUrl === this.currentTextureUrl && this.backgroundSprite && this.transitionState === 'idle') {
      console.log('[MapBackgroundLayer] Skipping redundant render (same URL already displayed)');
      return;
    }

    // Skip if we're already loading this exact URL (prevents race between subscription + useEffect)
    if (newUrl && newUrl === this.pendingTextureUrl) {
      console.log('[MapBackgroundLayer] Skipping render — already loading this URL');
      return;
    }

    // Cancel any in-flight transition
    this.cancelTransition();

    // Retain old sprite for transition (don't destroy yet)
    const hadPrevious = !!this.backgroundSprite;
    if (this.backgroundSprite) {
      this.previousSprite = this.backgroundSprite;
      this.backgroundSprite = null;
    }

    if (!map?.baseTexture) {
      console.log('[MapBackgroundLayer] No map or baseTexture, skipping render');
      // Clean up previous since there's nothing to transition to
      this.cleanupPrevious();
      this.currentTextureUrl = null;
      return;
    }

    const texture = map.baseTexture;

    // Reset synchronization flags
    this.blurOutComplete = false;
    this.newTextureReady = false;

    if (hadPrevious && this.previousSprite) {
      // Start blur-out on old sprite while loading new texture
      this.startBlurOut();
    }

    // Load the new texture (async)
    this.pendingTextureUrl = texture.colorUrl;
    this.loadTexture(texture.colorUrl, map.bounds, hadPrevious);
  }

  /**
   * Load and display a texture.
   * Uses a generation counter to discard stale loads.
   */
  private async loadTexture(
    url: string,
    bounds: SpriteBounds,
    hadPrevious: boolean
  ): Promise<void> {
    const gen = ++this.loadGeneration;
    console.log('[MapBackgroundLayer] loadTexture() gen', gen, 'url type:', url.startsWith('data:') ? 'base64' : 'URL');
    try {
      const texture = await PIXI.Texture.fromURL(url);

      // Discard if a newer render call has superseded this one
      if (gen !== this.loadGeneration) {
        console.log('[MapBackgroundLayer] Discarding stale texture load (gen', gen, 'vs', this.loadGeneration, ')');
        // Don't destroy — Pixi's texture cache may share this with the current load
        return;
      }

      // Guard against null texture (invalid URL, failed load)
      if (!texture || !texture.valid) {
        console.warn('[MapBackgroundLayer] Texture loaded but invalid, showing placeholder');
        texture?.destroy(true);
        this.cleanupPrevious();
        this.cancelTransition();
        this.createPlaceholderBackground(bounds);
        return;
      }

      // Create the new sprite (positioned but not yet visible)
      const sprite = this.createPositionedSprite(texture, bounds);

      if (hadPrevious && this.previousSprite) {
        // Store pending sprite and signal texture ready
        this.pendingSprite = sprite;
        this.pendingBounds = bounds;
        this.newTextureReady = true;
        this.tryStartWipe();
      } else {
        // First load — add sprite and fade in
        this.backgroundSprite = sprite;
        this.container.addChild(sprite);
        this.startFadeIn();
      }

      this.currentTextureUrl = url;
      this.pendingTextureUrl = null;
      console.log('[MapBackgroundLayer] Loaded texture gen', gen);
    } catch (error) {
      if (gen === this.loadGeneration) {
        console.error('[MapBackgroundLayer] Failed to load texture:', error);
        this.pendingTextureUrl = null;
        this.cleanupPrevious();
        this.cancelTransition();
        this.createPlaceholderBackground(bounds);
      }
    }
  }

  /**
   * Create a sprite positioned according to world bounds
   */
  private createPositionedSprite(texture: PIXI.Texture, bounds: SpriteBounds): PIXI.Sprite {
    const sprite = new PIXI.Sprite(texture);
    const widthPixels = bounds.width * this.pixelsPerFoot;
    const heightPixels = bounds.height * this.pixelsPerFoot;
    const originXPixels = bounds.originX * this.pixelsPerFoot;
    const originYPixels = bounds.originY * this.pixelsPerFoot;

    sprite.x = originXPixels;
    sprite.y = originYPixels;
    sprite.width = widthPixels;
    sprite.height = heightPixels;
    sprite.zIndex = -1;

    return sprite;
  }

  // ── Blur Out Phase ──────────────────────────────────────────

  private startBlurOut(): void {
    if (!this.previousSprite) return;

    this.transitionState = 'blur_out';
    this.blurFilter = new PIXI.BlurFilter(0);
    this.blurFilter.quality = 3;
    this.previousSprite.filters = [this.blurFilter];
    this.transitionStartTime = performance.now();

    this.transitionTickerFn = () => this.tickBlurOut();
    PIXI.Ticker.shared.add(this.transitionTickerFn);

    console.log('[MapBackgroundLayer] Starting blur-out transition');
  }

  private tickBlurOut(): void {
    const elapsed = performance.now() - this.transitionStartTime;
    const t = Math.min(elapsed / BLUR_DURATION_MS, 1);
    const eased = easeInOutCubic(t);

    if (this.blurFilter) {
      this.blurFilter.blur = eased * MAX_BLUR_STRENGTH;
    }

    if (t >= 1) {
      // Blur complete — remove ticker
      if (this.transitionTickerFn) {
        PIXI.Ticker.shared.remove(this.transitionTickerFn);
        this.transitionTickerFn = null;
      }
      this.blurOutComplete = true;
      this.tryStartWipe();
    }
  }

  // ── Synchronization Barrier ─────────────────────────────────

  /**
   * Called when blur finishes or texture loads — starts wipe only when both are ready
   */
  private tryStartWipe(): void {
    if (!this.blurOutComplete || !this.newTextureReady) return;
    if (!this.pendingSprite) return;

    this.startWipeReveal();
  }

  // ── Wipe Reveal Phase ───────────────────────────────────────

  private startWipeReveal(): void {
    if (!this.pendingSprite || !this.pendingBounds) return;

    this.transitionState = 'wipe_reveal';

    // Add the new sprite to the container (above previous)
    this.pendingSprite.zIndex = -1;
    this.container.addChild(this.pendingSprite);

    // Create mask graphics — starts at width=0 (nothing visible)
    const widthPx = this.pendingBounds.width * this.pixelsPerFoot;
    const heightPx = this.pendingBounds.height * this.pixelsPerFoot;
    const originXPx = this.pendingBounds.originX * this.pixelsPerFoot;
    const originYPx = this.pendingBounds.originY * this.pixelsPerFoot;

    this.wipeMask = new PIXI.Graphics();
    this.wipeMask.beginFill(0xffffff);
    this.wipeMask.drawRect(0, 0, 0, heightPx);
    this.wipeMask.endFill();
    this.wipeMask.x = originXPx;
    this.wipeMask.y = originYPx;

    this.container.addChild(this.wipeMask);
    this.pendingSprite.mask = this.wipeMask;

    this.transitionStartTime = performance.now();

    this.transitionTickerFn = () => this.tickWipeReveal(widthPx, heightPx, originXPx, originYPx);
    PIXI.Ticker.shared.add(this.transitionTickerFn);

    console.log('[MapBackgroundLayer] Starting wipe-reveal transition');
  }

  private tickWipeReveal(
    widthPx: number,
    heightPx: number,
    originXPx: number,
    originYPx: number
  ): void {
    const elapsed = performance.now() - this.transitionStartTime;
    const t = Math.min(elapsed / WIPE_DURATION_MS, 1);
    const eased = easeOutCubic(t);

    // Redraw mask with expanding width
    if (this.wipeMask) {
      this.wipeMask.clear();
      this.wipeMask.beginFill(0xffffff);
      this.wipeMask.drawRect(0, 0, eased * widthPx, heightPx);
      this.wipeMask.endFill();
      this.wipeMask.x = originXPx;
      this.wipeMask.y = originYPx;
    }

    if (t >= 1) {
      this.finalizeTransition();
    }
  }

  // ── Fade In (first load, no previous map) ───────────────────

  private startFadeIn(): void {
    if (!this.backgroundSprite) return;

    this.transitionState = 'fade_in';
    this.backgroundSprite.alpha = 0;
    this.transitionStartTime = performance.now();

    this.transitionTickerFn = () => this.tickFadeIn();
    PIXI.Ticker.shared.add(this.transitionTickerFn);

    console.log('[MapBackgroundLayer] Starting fade-in transition');
  }

  private tickFadeIn(): void {
    const elapsed = performance.now() - this.transitionStartTime;
    const t = Math.min(elapsed / FADE_DURATION_MS, 1);
    const eased = easeOutCubic(t);

    if (this.backgroundSprite) {
      this.backgroundSprite.alpha = eased;
    }

    if (t >= 1) {
      if (this.backgroundSprite) {
        this.backgroundSprite.alpha = 1;
      }
      if (this.transitionTickerFn) {
        PIXI.Ticker.shared.remove(this.transitionTickerFn);
        this.transitionTickerFn = null;
      }
      this.transitionState = 'idle';
      console.log('[MapBackgroundLayer] Fade-in complete');
    }
  }

  // ── Cleanup / Finalize ──────────────────────────────────────

  /**
   * Called when wipe reveal completes — promote pending sprite, clean up old
   */
  private finalizeTransition(): void {
    // Remove ticker
    if (this.transitionTickerFn) {
      PIXI.Ticker.shared.remove(this.transitionTickerFn);
      this.transitionTickerFn = null;
    }

    // Remove mask from new sprite
    if (this.pendingSprite) {
      this.pendingSprite.mask = null;
    }

    // Destroy mask graphics
    if (this.wipeMask) {
      this.container.removeChild(this.wipeMask);
      this.wipeMask.destroy();
      this.wipeMask = null;
    }

    // Promote pending → current
    this.backgroundSprite = this.pendingSprite;
    this.pendingSprite = null;
    this.pendingBounds = null;

    // Destroy old blurred sprite
    this.cleanupPrevious();

    // Reset flags
    this.blurOutComplete = false;
    this.newTextureReady = false;
    this.transitionState = 'idle';

    console.log('[MapBackgroundLayer] Wipe-reveal complete');
  }

  /**
   * Cancel any in-flight transition — clean teardown for rapid refresh
   */
  private cancelTransition(): void {
    if (this.transitionState === 'idle') return;

    console.log('[MapBackgroundLayer] Cancelling transition (state:', this.transitionState, ')');

    // Remove ticker
    if (this.transitionTickerFn) {
      PIXI.Ticker.shared.remove(this.transitionTickerFn);
      this.transitionTickerFn = null;
    }

    // If we have a pending sprite mid-wipe, promote it immediately
    if (this.pendingSprite) {
      this.pendingSprite.mask = null;
      // If it's not in the container yet, add it
      if (!this.pendingSprite.parent) {
        this.container.addChild(this.pendingSprite);
      }
      this.pendingSprite.alpha = 1;
      this.backgroundSprite = this.pendingSprite;
      this.pendingSprite = null;
      this.pendingBounds = null;
    }

    // Clean up mask
    if (this.wipeMask) {
      this.container.removeChild(this.wipeMask);
      this.wipeMask.destroy();
      this.wipeMask = null;
    }

    // Clean up previous
    this.cleanupPrevious();

    // Reset flags
    this.blurOutComplete = false;
    this.newTextureReady = false;
    this.transitionState = 'idle';
  }

  /**
   * Destroy old sprite + blur filter.
   * IMPORTANT: Never destroy the texture — it may be shared with the current
   * sprite via Pixi's texture cache (same URL loaded twice). The texture will
   * be garbage collected when no sprites reference it.
   */
  private cleanupPrevious(): void {
    if (this.previousSprite) {
      this.previousSprite.filters = null;
      this.container.removeChild(this.previousSprite);
      this.previousSprite.destroy();
      this.previousSprite = null;
    }
    if (this.blurFilter) {
      this.blurFilter.destroy();
      this.blurFilter = null;
    }
  }

  /**
   * Update pixels per foot scaling
   */
  setPixelsPerFoot(pixelsPerFoot: number): void {
    this.pixelsPerFoot = pixelsPerFoot;
    const { activeMap } = useMapStore.getState();
    this.render(activeMap);
  }

  /**
   * Force refresh from current store state
   */
  refresh(): void {
    const { activeMap } = useMapStore.getState();
    this.render(activeMap);
  }

  /**
   * Show a placeholder background when the real texture fails to load.
   * Subtle parchment with grid and gold border — no text label.
   */
  private createPlaceholderBackground(bounds: SpriteBounds): void {
    const widthPx = bounds.width * this.pixelsPerFoot;
    const heightPx = bounds.height * this.pixelsPerFoot;
    const originXPx = bounds.originX * this.pixelsPerFoot;
    const originYPx = bounds.originY * this.pixelsPerFoot;

    const g = new PIXI.Graphics();

    // Dark parchment fill
    g.beginFill(0x2a231a);
    g.drawRect(0, 0, widthPx, heightPx);
    g.endFill();

    // Subtle grid
    g.lineStyle(1, 0x3a332a, 0.3);
    const gridSize = 5 * this.pixelsPerFoot; // 5ft grid
    for (let x = 0; x <= widthPx; x += gridSize) {
      g.moveTo(x, 0);
      g.lineTo(x, heightPx);
    }
    for (let y = 0; y <= heightPx; y += gridSize) {
      g.moveTo(0, y);
      g.lineTo(widthPx, y);
    }

    // Gold border
    g.lineStyle(3, 0xd4af37, 0.6);
    g.drawRect(4, 4, widthPx - 8, heightPx - 8);

    // Small warning triangle in bottom-right corner instead of text
    const iconSize = 16;
    const iconX = widthPx - 28;
    const iconY = heightPx - 28;
    g.lineStyle(2, 0xd4af37, 0.5);
    g.beginFill(0x000000, 0);
    g.moveTo(iconX, iconY + iconSize);
    g.lineTo(iconX + iconSize / 2, iconY);
    g.lineTo(iconX + iconSize, iconY + iconSize);
    g.closePath();
    g.endFill();

    g.x = originXPx;
    g.y = originYPx;
    g.zIndex = -1;

    this.container.addChild(g);
    console.log('[MapBackgroundLayer] Showing placeholder background');
  }

  /**
   * Clean up
   */
  destroy(): void {
    this.cancelTransition();
    this.unsubscribe?.();

    if (this.backgroundSprite) {
      this.backgroundSprite.destroy({ texture: true });
    }

    this.container.destroy({ children: true });
  }
}
